import React, { ChangeEventHandler, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { brandColors } from '@/app/constants';
import CircleWarningIcon from '@/component-library/primitives/Icons/CircleWarningIcon/CircleWarningIcon';
import InfoIcon from '@/component-library/primitives/Icons/InfoIcon/InfoIcon';
import Tooltip from '@/component-library/primitives/Tooltip/Tooltip';

import HideIcon from '../../primitives/Icons/HideIcon/HideIcon';
import ShowIcon from '../../primitives/Icons/ShowIcon/ShowIcon';

export type InputSize = 'large' | 'small';
interface InputProps {
  value?: string;
  size?: InputSize;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onFocus?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: ChangeEventHandler<HTMLInputElement>;
  placeholder?: string;
  error?: string;
  type?: 'text' | 'password' | 'datetime-local' | 'number' | 'email';
  className?: string;
  hasInfoIcon?: boolean;
  tooltipContent?: React.ReactNode;
  maxLength?: number;
  label?: string;
  optional?: boolean;
  disabled?: boolean;
  currencyLeft?: string;
  currencyRight?: string;
  iconLeft?: React.ReactElement;
  iconRight?: React.ReactElement;
  avatar?: React.ReactElement;
  withBorder?: boolean;
  readOnly?: boolean;
  fullWidth?: boolean;
}

export const Input = forwardRef<HTMLDivElement, InputProps>(
  (
    {
      value,
      size = 'small',
      onChange,
      onBlur,
      onFocus,
      placeholder,
      error,
      type = 'text',
      className = '',
      hasInfoIcon,
      tooltipContent,
      optional = false,
      disabled = false,
      label,
      currencyLeft,
      currencyRight,
      iconLeft,
      iconRight,
      avatar,
      withBorder = false,
      readOnly = false,
      fullWidth,
      ...rest
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const [isIconHovered, setIsIconHovered] = useState(false);
    const [isTooltipDisplayed, setIsTooltipDisplayed] = useState(false);
    const [isOverflown, setIsOverflown] = useState(false);

    const { t } = useTranslation();

    const togglePasswordVisibility = (e: { preventDefault: () => void }) => {
      e.preventDefault();
      setShowPassword(!showPassword);
    };

    const internalRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => internalRef.current as HTMLInputElement);

    useEffect(() => {
      const element = internalRef.current!;
      setIsOverflown(element.scrollWidth > element.offsetWidth);
    }, [internalRef.current?.value]);

    return (
      <div className={`${fullWidth && 'w-full'}`}>
        <div className={`flex flex-col relative`}>
          {label && (
            <label
              className={`block text-sm mb-1 font-medium
                ${error ? 'text-custom-red' : 'text-custom-darkSilver'}
                ${disabled && 'opacity-50'}
              `}
            >
              {label} {optional && <span>({t('InputField.optionalLabel')})</span>}
            </label>
          )}
          <Tooltip
            content={internalRef.current?.value}
            open={isTooltipDisplayed}
            onOpenChange={(open) => {
              if (open && isOverflown) {
                setIsTooltipDisplayed(true);
              } else {
                setIsTooltipDisplayed(false);
              }
            }}
            contentClassName="!bg-custom-darkBlue p-3 rounded-lg z-50 text-gray-50 text-xs"
            arrowClassName="fill-custom-darkBlue w-3 h-2"
          >
            <input
              type={showPassword ? 'text' : type}
              value={value}
              onChange={onChange}
              onFocus={onFocus}
              onBlur={onBlur}
              placeholder={placeholder}
              ref={internalRef}
              disabled={disabled}
              readOnly={readOnly}
              className={`relative border text-ellipsis text-custom-black text-sm focus:outline-none focus:border-custom-darkSilver placeholder:focus:text-transparent placeholder:text-custom-darkSilver bg-custom-silver rounded-lg py-3 
                ${disabled && 'opacity-50'} 
                ${size === 'small' ? 'h-12' : 'h-14'} 
                ${error && '!border-custom-red'} 
                ${currencyLeft ? 'pl-9' : 'pl-3'} 
                ${iconLeft ? '!pl-11' : 'pl-3'} 
                ${avatar ? '!pl-16' : 'pl-3'} 
                ${currencyRight ? '!pr-9' : 'pr-3'} 
                ${type === 'password' || hasInfoIcon || iconRight ? '!pr-11' : 'pr-3'} 
                ${
                  internalRef.current?.value && internalRef.current?.value.length > 0
                    ? 'border-primary-150 hover:border-primary-150'
                    : 'border-transparent'
                } 
                ${withBorder && !error && `!border-primary-100 hover:!border-gray-800 focus:!border-primary-200`} 
                ${withBorder && error && '!border-custom-red'} 
                ${
                  readOnly &&
                  '!border-none hover:!border-none focus:!border-none placeholder:!text-gray-500 placeholder:!focus:text-gray-500'
                } 
                ${className} 
              `}
              {...rest}
            />
          </Tooltip>
          {currencyLeft && (
            <div
              className={`absolute top-1/2 left-4 text-gray-500 text-base bg-gray-800 ${
                internalRef.current?.value && internalRef.current?.value.length > 0 && '!text-gray-100'
              } ${error && '!text-custom-red'}`}
            >
              {currencyLeft}
            </div>
          )}
          {iconLeft && (
            <div
              className={`absolute top-1/2 left-4 bg-darkBlue ${
                internalRef.current?.value && internalRef.current?.value.length > 0 && '!text-gray-100'
              } ${error && '!text-custom-red'}`}
            >
              <div
                className={`[&>*_path]:stroke-gray-500 ${
                  internalRef.current?.value && internalRef.current?.value.length > 0 && '[&>*_path]:!stroke-gray-100'
                } ${error && '[&>*_path]:!stroke-custom-red'}`}
              >
                {iconLeft}
              </div>
            </div>
          )}
          {avatar && <div className={`absolute top-[39%] left-4 bg-darkBlue`}>{avatar}</div>}
          {currencyRight && (
            <div
              className={`absolute top-1/2 right-4 text-gray-500 text-base bg-darkBlue ${
                internalRef.current?.value && internalRef.current?.value.length > 0 && '!text-gray-100'
              } ${error && '!text-custom-red'}`}
            >
              {currencyRight}
            </div>
          )}
          {iconRight && (
            <div
              className={`absolute top-1/2 right-4 bg-darkBlue ${
                internalRef.current?.value && internalRef.current?.value.length > 0 && '!text-gray-100'
              } ${error && '!text-custom-red'}`}
            >
              <div
                className={`[&>*_path]:stroke-gray-500 ${
                  internalRef.current?.value && internalRef.current?.value.length > 0 && '[&>*_path]:!stroke-gray-100'
                } ${error && '[&>*_path]:!stroke-custom-red'}`}
              >
                {iconRight}
              </div>
            </div>
          )}
          {type === 'password' && (
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute bottom-3 right-4 focus:outline-none h-6"
            >
              {showPassword ? (
                <ShowIcon color={error ? brandColors.red : brandColors.darkSilver} onClick={togglePasswordVisibility} />
              ) : (
                <HideIcon color={error ? brandColors.red : brandColors.darkSilver} onClick={togglePasswordVisibility} />
              )}
            </button>
          )}
          {hasInfoIcon && (
            <Tooltip
              content={tooltipContent}
              open={isIconHovered}
              onOpenChange={(open) => {
                if (open) {
                  setIsIconHovered(true);
                } else {
                  setIsIconHovered(false);
                }
              }}
              contentClassName="bg-custom-aliceBlue p-6 rounded-lg z-50 max-w-xs"
              arrowClassName="fill-custom-aliceBlue w-5 h-2"
            >
              <button
                type="button"
                className="absolute top-1/2 right-4 transform -translate-y-1/2 focus:outline-none h-6"
              >
                <InfoIcon />
              </button>
            </Tooltip>
          )}
        </div>
        {error && (
          <p className="text-custom-red mt-1 text-xs font-medium flex flex-row items-center gap-1">
            {error.trim().length > 0 && <CircleWarningIcon color={brandColors.red} />} {error}
          </p>
        )}
      </div>
    );
  },
);
